import * as React from 'react';
import { cn } from '../../lib/utils';

/* ============================================================================
   ModalPassos - cadastro longo, agora em TRILHA LATERAL.

   HISTORICO, porque a decisao mudou tres vezes e o motivo importa:
     1. faixa de CARTOES lado a lado (todos visiveis, rolagem horizontal);
     2. 18/08/2026 - ABAS horizontais no topo;
     3. 26/08/2026 - TRILHA LATERAL a esquerda, com o desenho aprovado.

   O nome do arquivo e o contrato de props continuam iguais: os seis modais que
   usam isto (usuario, grupo, aplicativo, APK, regional, politica) nao precisaram
   de nenhuma mudanca obrigatoria. Tudo que a trilha acrescenta - grupo do item,
   resumo do item, identidade no cabecalho - e OPCIONAL, e cada modal pode ser
   enriquecido depois, um por vez.

   O QUE A TRILHA RESOLVE que as abas horizontais nao resolviam:

   1. CABE O RESUMO. Numa aba de 90px so cabe o rotulo. Na trilha cada item tem
      duas linhas, e a de baixo diz o ESTADO daquele bloco ("3 selecionadas", "nao
      definido", "MASTER · ATIVO"). Isso responde "o que ja esta preenchido?" sem
      abrir bloco nenhum - que era a pergunta que os ✓ verdes tentavam responder e
      nao conseguiam, porque um circulo nao diz o que tem dentro.

   2. AGRUPA. "Trocar senha" e "Historico" nao sao campos do cadastro, sao
      ferramentas. Na tira horizontal eles ficavam lado a lado com "Dados" e
      "Regionais" como se fossem a mesma coisa. Na trilha viram um grupo proprio,
      com titulo.

   3. NAO ROLA NA HORIZONTAL. Com sete blocos a tira horizontal comecava a esconder
      itens no fim, e rolagem horizontal em formulario e gesto que quase ninguem
      faz.

   O QUE MUDOU DE LUGAR: o botao primario saiu do CABECALHO e foi para o RODAPE,
   junto do estado do formulario e do Cancelar. Motivo: no cabecalho ele ficava
   acima do conteudo que ele salva, e ao lado do X de fechar - dois botoes de
   consequencia oposta a dois centimetros um do outro. No rodape a leitura fica na
   ordem natural: preencho, vejo o estado, salvo.

   DECISAO TECNICA que continua valendo: os paineis inativos ficam MONTADOS e
   escondidos por CSS, nao desmontados. Blocos com estado interno (a busca do
   seletor de grupo) voltariam ao zero a cada troca, e o historico faz requisicao
   ao montar - desmontar significaria uma consulta nova por visita.

   SEM NENHUMA BORDA NA MOLDURA (10/09/2026, "quero 100% sem borda"). Sairam o
   risco vertical entre trilha e conteudo, o de cima do rodape, o do card e o do
   item selecionado. Nada disso era necessario: a trilha ja se separa pelo fundo
   mais escuro, o rodape pelo mesmo recurso, o card pela sombra sobre o fundo
   escurecido e o item ativo pela superficie clara com sombra. Onde ja ha diferenca
   de superficie, borda e um segundo contorno dizendo o que a primeira ja disse.

   O que NAO saiu foram as bordas de CAMPO (input, seletor, botao secundario):
   ali a borda nao e enfeite de moldura, e o que diz "aqui se digita". Campo sem
   contorno vira texto solto no meio do formulario.

   RESPONSIVO: abaixo de `md` a trilha nao cabe ao lado do formulario, e volta a
   ser uma tira horizontal no topo. Nao e o desenho aprovado, e um formulario de
   400px de largura com 230px de trilha do lado seria pior que isso.
   ============================================================================ */

export type PassoModal = {
  id: string;
  titulo: string;
  /**
   * ACEITO E IGNORADO desde a versao em abas - ver o historico no topo. Continua
   * no tipo porque os seis modais ainda passam, e tirar seria mexer em seis
   * arquivos para nada.
   */
  /** `true` libera o ✓ verde. Deve refletir a validacao real do bloco. */
  valido?: boolean;
  /** Texto do que falta, mostrado no rodape. */
  pendencia?: string;
  /**
   * Bloco que NAO participa da validacao nem do gate de salvar. Para ferramenta,
   * nao campo: trocar senha, ver historico.
   */
  opcional?: boolean;
  /** Bloco de LEITURA: nao valida e nao recebe marca nenhuma. */
  informativo?: boolean;
  /**
   * Titulo do GRUPO na trilha ("Cadastro", "Ferramentas"). Itens sem grupo ficam
   * num primeiro grupo sem titulo - e o que mantem os modais que ainda nao
   * declararam nada funcionando igual.
   */
  grupo?: string;
  /**
   * Uma linha dizendo o ESTADO deste bloco, embaixo do titulo na trilha:
   * "3 selecionadas", "nao definido", "6 eventos".
   *
   * E o ganho principal da trilha. Sem isto o item mostra so o titulo, o que
   * funciona mas devolve a trilha ao mesmo pouco que a aba dizia.
   */
  resumo?: React.ReactNode;
  conteudo: React.ReactNode;
};

/** Iniciais para o avatar do cabecalho: "Bruna Soares" -> "BS". */
function iniciais(nome: string): string {
  const partes = String(nome || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!partes.length) return '?';
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

/* ============================================================================
   ICONE POR PASSO, aqui e nao em cada modal.

   Os seis modais declaram `icone` de formas diferentes - alguns com emoji, e o de
   politica nao declarava nenhum, que era o motivo de a trilha dele aparecer so com
   as marcas de estado. Centralizar resolve os dois problemas: todo passo ganha
   icone, e o desenho e o MESMO para o mesmo assunto em qualquer modal - "Historico"
   e o mesmo relogio no usuario, no grupo e na politica.

   SAO GLIFOS DE TRACO, e nao emoji. Emoji e desenhado pelo sistema operacional:
   muda de forma entre Windows, Mac e Android, chega colorido no meio de uma
   interface monocromatica e nao acompanha a cor do estado. Um `<path>` acompanha.

   A chave e o `id` do passo, que ja existe e ja e estavel (os modais usam para
   `aria-controls`). Id sem entrada aqui cai no ponto neutro - nao quebra nada.
   ============================================================================ */
const GLIFOS: Record<string, string> = {
  // etiqueta
  identificacao: 'M3 3h6l6 6-6 6-6-6V3z M6.2 6.2h.01',
  // grade de aplicativos
  aplicativos: 'M3 3h4v4H3V3z M11 3h4v4h-4V3z M3 11h4v4H3v-4z M11 11h4v4h-4v-4z',
  // celular travado (kiosk)
  kiosk: 'M6 2h6a1 1 0 011 1v12a1 1 0 01-1 1H6a1 1 0 01-1-1V3a1 1 0 011-1z M8 13h2',
  // cadeado
  bloqueios: 'M5 8h8v7H5V8z M7 8V5.5a2 2 0 014 0V8',
  // globo cortado
  sites: 'M9 2.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z M2.5 9h13 M9 2.5c-2 2.2-2 9.3 0 13 2-3.7 2-10.8 0-13',
  // engrenagem simplificada
  padroes: 'M9 6.6a2.4 2.4 0 100 4.8 2.4 2.4 0 000-4.8z M9 2.4v1.8 M9 13.8v1.8 M2.4 9h1.8 M13.8 9h1.8 M4.3 4.3l1.3 1.3 M12.4 12.4l1.3 1.3 M13.7 4.3l-1.3 1.3 M5.6 12.4l-1.3 1.3',
  // relogio
  historico: 'M9 2.5a6.5 6.5 0 100 13 6.5 6.5 0 000-13z M9 5.5V9l2.5 1.6',
  // pessoa
  dados: 'M9 3.2a2.6 2.6 0 100 5.2 2.6 2.6 0 000-5.2z M3.8 15c0-3 2.6-4.4 5.2-4.4S14.2 12 14.2 15',
  // chave
  acesso: 'M11.5 3.5a3.5 3.5 0 11-3.2 4.9L3 13.7V15.5h2.2v-1.6h1.6v-1.6h1.6l.9-.9A3.5 3.5 0 1011.5 3.5z',
  // mapa
  regionais: 'M2.6 4.6l4.2-1.8 4.4 1.8 4.2-1.8v10.6l-4.2 1.8-4.4-1.8-4.2 1.8V4.6z M6.8 2.8v10.6 M11.2 4.6v10.6',
  regional: 'M2.6 4.6l4.2-1.8 4.4 1.8 4.2-1.8v10.6l-4.2 1.8-4.4-1.8-4.2 1.8V4.6z M6.8 2.8v10.6 M11.2 4.6v10.6',
  // predio
  grupos: 'M3.5 15.5V4l5-1.8V15.5 M8.5 6.6l6 1.6v7.3 M3.5 15.5h11 M5.6 6.4h.01 M5.6 9.2h.01 M5.6 12h.01',
  // etiqueta de custo
  cr: 'M9 2.8v12.4 M11.8 5.6H7.6a2 2 0 000 4h2.8a2 2 0 010 4H5.8',
  // escudo
  seguranca: 'M9 2.4l5.4 2.2v4.2c0 3.4-2.4 5.6-5.4 6.8-3-1.2-5.4-3.4-5.4-6.8V4.6L9 2.4z',
  // caixa de pacote
  apks: 'M9 2.4l6 3v7.2l-6 3-6-3V5.4l6-3z M3 5.4l6 3 6-3 M9 8.4v7.2',
  // arquivo
  arquivo: 'M5 2.5h5l3 3v10H5v-13z M10 2.5v3.2h3',
  // celular com sinal
  provisionamento: 'M6 2.5h6a1 1 0 011 1v11a1 1 0 01-1 1H6a1 1 0 01-1-1v-11a1 1 0 011-1z M8.4 13h1.2',
};

function Glifo({ id }: { id: string }) {
  const d = GLIFOS[id];
  if (!d) return <span className="h-2 w-2 rounded-full bg-current opacity-50" aria-hidden />;
  return (
    <svg width="15" height="15" viewBox="0 0 18 18" fill="none" aria-hidden>
      <path d={d} stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function ModalPassos({
  aberto,
  titulo,
  subtitulo,
  nome,
  selos,
  meta,
  passos,
  rotuloFinalizar = 'Finalizar',
  onFinalizar,
  onFechar,
  salvando = false,
  erro,
  acaoExtra,
}: {
  aberto: boolean;
  titulo: string;
  subtitulo?: string;
  /**
   * Nome do REGISTRO sendo editado ("Bruna Soares"), que no desenho aprovado e o
   * texto grande do cabecalho, com avatar de iniciais.
   *
   * Ausente = o cabecalho mostra `titulo`/`subtitulo` como antes. E por isso que
   * os seis modais continuam funcionando sem mudanca.
   */
  nome?: string;
  /** Selos ao lado do nome (status, perfil). */
  selos?: React.ReactNode;
  /** Linha de apoio em mono: e-mail, codigo, ultimo acesso. */
  meta?: React.ReactNode;
  passos: PassoModal[];
  rotuloFinalizar?: string;
  onFinalizar: () => void;
  onFechar: () => void;
  salvando?: boolean;
  /**
   * Falha do SERVIDOR ao salvar - "Ja existe esse aplicativo registrado", por
   * exemplo.
   *
   * Precisa viver no rodape, e nao dentro de um passo: quando o salvamento falha,
   * a pessoa esta olhando o botao que acabou de clicar. Mensagem escondida numa
   * aba que talvez nem esteja aberta e mensagem que ninguem le - e a tela fica
   * parecendo que simplesmente nao salvou.
   *
   * Diferente das PENDENCIAS, que sao do formulario e o rodape ja resume: esta e
   * resposta do servidor, e por isso aparece em vermelho no lugar do estado.
   */
  erro?: string;
  /**
   * Acao secundaria, hoje o "Salvar e forcar APK nova" (so Master). Vai para o
   * RODAPE, a esquerda do Cancelar - longe do botao primario de proposito: acao de
   * excecao encostada no Salvar acaba clicada por engano.
   */
  acaoExtra?: React.ReactNode;
}) {
  const [ativo, setAtivo] = React.useState(0);
  const refTrilha = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (aberto) setAtivo(0);
  }, [aberto]);

  // Esc fecha - o teclado precisa ter saida sem procurar o X.
  React.useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (e: KeyboardEvent) => e.key === 'Escape' && !salvando && onFechar();
    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [aberto, onFechar, salvando]);

  // O corpo da pagina para de rolar enquanto o modal esta aberto; sem isso o
  // scroll "vaza" para a lista atras e o usuario perde o lugar onde estava.
  React.useEffect(() => {
    if (!aberto) return;
    const anterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = anterior;
    };
  }, [aberto]);

  // Blocos opcionais e informativos ficam FORA da conta: nao travam o salvar e nao
  // entram na lista de pendencias.
  const obrigatorios = passos.filter((p) => !p.opcional && !p.informativo);
  const validos = obrigatorios.filter((p) => p.valido).length;
  const tudoValido = obrigatorios.length > 0 && validos === obrigatorios.length;
  const pendencias = obrigatorios.filter((p) => !p.valido);
  const textoPendencias = pendencias.map((p) => p.pendencia || p.titulo).join(', ');

  const indiceValido = Math.min(ativo, Math.max(0, passos.length - 1));

  /* Agrupa PRESERVANDO A ORDEM em que os passos chegaram - `Map` mantem ordem de
     insercao. Ordenar por nome de grupo mudaria a sequencia do formulario, que e
     decisao de quem escreveu o modal. */
  const grupos = React.useMemo(() => {
    const mapa = new Map<string, Array<{ passo: PassoModal; indice: number }>>();
    passos.forEach((passo, indice) => {
      const chave = passo.grupo || '';
      if (!mapa.has(chave)) mapa.set(chave, []);
      mapa.get(chave)!.push({ passo, indice });
    });
    return Array.from(mapa.entries());
  }, [passos]);

  /** Setas navegam na trilha - padrao ARIA de tablist. */
  function aoTeclarNaTrilha(e: React.KeyboardEvent) {
    const ultimo = passos.length - 1;
    let alvo: number | null = null;
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') alvo = indiceValido >= ultimo ? 0 : indiceValido + 1;
    if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') alvo = indiceValido <= 0 ? ultimo : indiceValido - 1;
    if (e.key === 'Home') alvo = 0;
    if (e.key === 'End') alvo = ultimo;
    if (alvo === null) return;
    e.preventDefault();
    setAtivo(alvo);
    const botao = refTrilha.current?.querySelector<HTMLElement>(`[data-indice="${alvo}"]`);
    botao?.focus();
    botao?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  if (!aberto) return null;

  /** Marca de estado do item da trilha. */
  function Marca({ passo }: { passo: PassoModal }) {
    // Informativo e opcional nao validam nada: um ✓ ali estragaria o significado
    // do ✓ nos blocos que de fato validam.
    if (passo.informativo || passo.opcional) {
      return (
        <span
          className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border border-line text-[10px] text-faint"
          aria-hidden
        >
          –
        </span>
      );
    }
    return passo.valido ? (
      <span
        className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full bg-ok-soft text-[10px] font-bold text-ok"
        aria-hidden
      >
        ✓
      </span>
    ) : (
      <span
        className="mt-0.5 grid h-4 w-4 flex-none place-items-center rounded-full border border-dashed border-line text-[10px] text-faint"
        aria-hidden
        title="Falta preencher"
      >
        •
      </span>
    );
  }

  return (
    /* Fundo escurecido, nao superficie opaca: com `bg-fundo` o modal cobria a tela
       com a mesma cor da pagina e parecia ter NAVEGADO para outro lugar.

       `bg-black/40` e nao `bg-fundo/40`: os tokens --gps-* sao cor completa, nao
       canais HSL, e nao aceitam modificador de opacidade. E `/40` porque `/45` NAO
       existe na escala do Tailwind - nao geraria regra e o fundo sairia
       transparente, sem erro no build. */
    <div
      className="fixed inset-0 z-[1400] grid place-items-center bg-black/40 p-4 backdrop-blur-[2px]"
      /* NAO FECHA ENQUANTO SALVA - nem por clique no fundo, nem por Escape, nem
         pelo X, nem pelo Cancelar.

         O pedido foi "travar a tela", e a razao de ser certo: entre o clique em
         Salvar e a resposta, o formulario ja nao representa o que existe.
         Fechar no meio deixa a pessoa sem saber se gravou - e em politica e
         pior, porque o salvamento monta a politica e a envia ao Google; sair no
         meio nao cancela nada, so esconde o resultado. */
      onClick={() => !salvando && onFechar()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={nome || titulo}
        onClick={(e) => e.stopPropagation()}
        /* TAMANHO FIXO EM % DA TELA. Os blocos tem tamanhos muito diferentes -
           Identificacao tem 4 campos, Outros bloqueios tem 20, o Historico tem uma
           tabela paginada. Com altura livre, cada troca redimensionava a janela e o
           rodape subia e descia embaixo do cursor. Com altura fixa, o que rola e o
           corpo, e o rodape com o Salvar fica sempre no mesmo lugar. */
        aria-busy={salvando}
        className="relative flex h-[76vh] w-[80vw] max-w-[1330px] flex-col overflow-hidden rounded-lg bg-surface text-ink shadow-card"
      >
        {/* ------------------------------------------------------ CABECALHO */}
        {/* CABECALHO NA COR DA PALETA (10/09/2026).

            `bg-primary` + `text-primary-foreground`, e nao um tom fraco do
            primary: o preset padrao e azul-marinho escuro, e 10% dele sobre branco
            da CINZA, nao azul claro - erro que ja custou tres correcoes neste
            projeto (cabecalho de tabela, faixa superior e item de menu ativo).

            E o par completo: bloco que pinta o proprio fundo TEM que declarar a
            propria cor de texto, senao herda a de fora e some. `primary-foreground`
            e o contraste que cada preset define, entao isto continua legivel se a
            paleta mudar para uma cor clara. */}
        {/* CABECALHO CLARO (desenho aprovado em 10/09/2026).

            Ele foi pintado de `bg-primary` algumas horas antes, tambem a pedido, e
            agora volta a ser claro. Fica registrado o porque de o claro funcionar:
            o cabecalho nao precisa de peso proprio - quem tem que se destacar e o
            NOME do registro que esta sendo editado, e nome escuro sobre fundo claro
            tem mais contraste que branco sobre azul-marinho.

            A cor da paleta continua no modal: no icone, no item ativo da trilha e
            no botao de salvar.

            O RISCO EMBAIXO (`border-b`) e o unico contorno que sobrou na moldura, e
            ele nao e chrome: enquanto o cabecalho era azul, a propria cor separava
            titulo de conteudo; claro sobre claro, sem o risco o nome do registro
            flutua colado no primeiro campo do formulario. Divisao entre duas
            regioes com funcao diferente e informacao - o que se pediu para tirar
            eram as bordas que apenas circundavam. */}
        <header className="flex flex-none items-center gap-3 border-b border-line bg-surface px-5 py-3">
          {nome ? (
            <>
              <span
className="grid h-9 w-9 flex-none place-items-center rounded-lg bg-info-soft text-[12.5px] font-bold text-info"
                aria-hidden
              >
                {iniciais(nome)}
              </span>

              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="truncate text-[14px] font-extrabold tracking-[-0.02em]">{nome}</h2>
                  {selos}
                </div>
                {/* Linha de apoio: e-mail, codigo, hora. */}
                {meta && <div className="mt-0.5 truncate text-[11px] text-muted-foreground">{meta}</div>}
              </div>
            </>
          ) : (
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-[14px] font-extrabold tracking-[-0.02em]">{titulo}</h2>
              {subtitulo && <p className="truncate text-[12px] text-muted-foreground">{subtitulo}</p>}
            </div>
          )}

          <button
            type="button"
            onClick={onFechar}
            disabled={salvando}
            className="flex-none text-[14px] text-faint hover:text-ink disabled:opacity-30"
            aria-label="Fechar"
          >
            ✕
          </button>
        </header>

        {/* ------------------------------------- TRILHA (md+) E TIRA (abaixo) */}
        <div className="flex min-h-0 flex-1">
          <div
            role="tablist"
            aria-label={titulo}
            aria-orientation="vertical"
            ref={refTrilha}
            onKeyDown={aoTeclarNaTrilha}
            className="hidden w-[230px] flex-none overflow-y-auto bg-surface-2 p-2 md:block"
          >
            {/* Rotulo da secao. Sem grupo declarado o modal cai num unico grupo
                sem titulo, e ai nada aparece aqui - nenhum modal precisou mudar. */}
            {grupos.map(([nomeGrupo, itens]) => (
              <div key={nomeGrupo || 'sem-grupo'} className="mb-2 last:mb-0">
                {nomeGrupo && (
                  <div className="px-2 py-1.5 text-[10px] font-bold uppercase tracking-[0.09em] text-faint">
                    {nomeGrupo}
                  </div>
                )}

                {itens.map(({ passo, indice }) => {
                  const selecionado = indice === indiceValido;
                  return (
                    <button
                      key={passo.id}
                      role="tab"
                      type="button"
                      data-indice={indice}
                      id={`aba-${passo.id}`}
                      aria-selected={selecionado}
                      aria-controls={`painel-${passo.id}`}
                      tabIndex={selecionado ? 0 : -1}
                      onClick={() => setAtivo(indice)}
                      /* O item selecionado e um cartao claro dentro da trilha
                         escura - fundo de superficie, borda e sombra leve. E o
                         mesmo recurso que a lista usa para a linha em foco, e nao
                         depende de cor para funcionar. */
                      /* ATIVO EM AZUL DE VERDADE (`info`), e nao num tom do
                         primary: o preset padrao e azul-marinho escuro, e 10% dele
                         da cinza - o mesmo engano que ja custou tres correcoes
                         neste projeto. `info-soft` e um azul saturado da paleta,
                         entao o item ativo LE como azul. */
                      className={cn(
                        'mb-0.5 flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                        selecionado ? 'bg-info-soft' : 'hover:bg-surface/70'
                      )}
                    >
                      {/* ICONE + MARCA DE ESTADO no mesmo alvo (10/09/2026, "colocar
                          uns icones para ficar mais apresentavel").

                          O icone da a leitura rapida - o olho encontra "Sites
                          bloqueados" pela forma antes de ler -, e a marca de estado
                          continua sendo o que responde "falta alguma coisa aqui?".
                          Empilhados, ocupam o espaco de um so: a marca vira um selo
                          no canto do icone.

                          Sem icone declarado, so a marca aparece - nenhum modal
                          precisou mudar por causa disto. */}
                      {/* O ICONE CARREGA O ESTADO NA COR: verde quando o bloco esta
                          valido, neutro quando falta algo, apagado quando o bloco
                          nao valida nada (ferramenta, historico).

                          A marca continua no canto, pequena, porque COR SOZINHA nao
                          e sinal: quem nao distingue verde de cinza ficaria sem
                          resposta. Cor para quem varre a lista, forma para quem
                          precisa ter certeza. */}
                      <span className={cn('flex-none', selecionado ? 'text-info' : 'text-muted-foreground')}>
                        <Glifo id={passo.id} />
                      </span>

                      <span className="min-w-0 flex-1">
                        <span
                          className={cn(
                            'block truncate text-[12px]',
                            selecionado ? 'font-bold text-info' : 'font-semibold text-ink'
                          )}
                        >
                          {passo.titulo}
                        </span>
                        {passo.resumo && (
                          <span className="block truncate text-[11px] text-faint">{passo.resumo}</span>
                        )}
                      </span>

                      {/* ESTADO NUM PONTO A DIREITA, e nao num selo sobre o icone.
                          Com o icone agora sem fundo, o selo ficava boiando; a
                          direita ele vira uma coluna, e o olho varre os seis
                          estados de cima a baixo sem parar em cada linha.

                          `title` porque cor sozinha nao e sinal - quem nao
                          distingue verde de cinza precisa de outra via. */}
                      <Marca passo={passo} />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {/* Tira horizontal para tela estreita - a trilha de 230px ao lado de um
              formulario de 400px seria pior que rolar rotulos. */}
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              role="tablist"
              aria-label={titulo}
              className="flex flex-none gap-1 overflow-x-auto px-2 md:hidden"
            >
              {passos.map((p, i) => {
                const selecionada = i === indiceValido;
                return (
                  <button
                    key={p.id}
                    role="tab"
                    type="button"
                    aria-selected={selecionada}
                    onClick={() => setAtivo(i)}
                    className={cn(
                      '-mb-px inline-flex flex-none items-center gap-2 border-b-2 px-3 py-2 text-[12px] transition-colors',
                      selecionada
                        ? 'border-primary font-bold text-primary'
                        : 'border-transparent font-semibold text-muted-foreground hover:border-line hover:text-ink'
                    )}
                  >
                    <span className="whitespace-nowrap">{p.titulo}</span>
                  </button>
                );
              })}
            </div>

            {/* PAINEIS. Todos montados; os inativos escondidos por CSS - ver o
                comentario do topo. `hidden` e `display:none`, o que tambem tira o
                painel escondido da ordem de tabulacao. */}
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {passos.map((p, i) => (
                <div
                  key={p.id}
                  role="tabpanel"
                  id={`painel-${p.id}`}
                  aria-labelledby={`aba-${p.id}`}
                  hidden={i !== indiceValido}
                  className="space-y-3"
                >
                  {p.conteudo}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------- RODAPE */}
        <footer className="flex flex-none flex-wrap items-center justify-between gap-3 bg-surface-2 px-4 py-2.5">
          {/* O ESTADO EM FRASE, nao em barra de progresso. "3 de 3" obriga a
              lembrar quantos blocos existem e qual falta; a frase diz. Quando falta
              algo, ela NOMEIA - botao desabilitado sem explicacao e beco sem
              saida. */}
          {erro ? (
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-danger">
              <span
                className="grid h-4 w-4 flex-none place-items-center rounded-full bg-danger-soft text-[10px]"
                aria-hidden
              >
                !
              </span>
              {erro}
            </span>
          ) : tudoValido ? (
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-ok">
              <span className="grid h-4 w-4 flex-none place-items-center rounded-full bg-ok-soft text-[10px]" aria-hidden>
                ✓
              </span>
              Tudo preenchido — pronto para salvar
            </span>
          ) : (
            <span
              className="max-w-[520px] truncate text-[12px] text-muted-foreground"
              title={`Falta preencher: ${textoPendencias}`}
            >
              Falta preencher: <strong className="font-semibold text-ink">{textoPendencias}</strong>
            </span>
          )}

          <span className="flex flex-none items-center gap-2">
            {acaoExtra}

            <button
              type="button"
              onClick={onFechar}
              disabled={salvando}
              className="h-9 rounded-lg border border-line bg-surface px-4 text-[12.5px] font-semibold text-ink hover:border-primary disabled:opacity-40"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={onFinalizar}
              disabled={!tudoValido || salvando}
              title={tudoValido ? undefined : `Falta: ${textoPendencias}`}
              className={cn(
                'h-9 rounded-lg px-4 text-[12.5px] font-bold',
                tudoValido ? 'bg-primary text-primary-foreground' : 'border border-line bg-surface-2 text-faint'
              )}
            >
              {salvando ? (
                <span className="flex items-center gap-2">
                  <Giro />
                  Salvando...
                </span>
              ) : (
                rotuloFinalizar
              )}
            </button>
          </span>
        </footer>

        {/* TELA TRAVADA ENQUANTO SALVA.

            Antes, o unico sinal era a palavra "Salvando..." dentro do botao, no
            canto inferior direito - e o resto do modal continuava respondendo:
            dava para trocar de aba, editar campo, mexer na lista de aplicativos.
            Nada disso ia para o servidor (o corpo da requisicao ja tinha sido
            montado), entao a pessoa via a propria alteracao na tela e ela nao
            era gravada. Pior que nao poder mexer e mexer sem efeito.

            A camada cobre o dialogo inteiro e come o clique - nao e so visual.
            `cursor-wait` porque o ponteiro tambem e resposta, e em salvamento
            de politica a espera passa de um segundo com frequencia.

            Fica DEPOIS do rodape na arvore, sem z-index: no mesmo contexto de
            empilhamento, quem vem depois fica por cima. Um `z-` aqui so criaria
            mais um numero para alguem ter que respeitar. */}
        {salvando && (
          <div
            className="absolute inset-0 grid cursor-wait place-items-center"
            /* A camada nao e anunciada: quem usa leitor de tela ja recebe o
               aviso pelo `aria-busy` do dialogo. Dois avisos para o mesmo
               estado atrapalham mais que informam. */
            aria-hidden
          >
            <span className="absolute inset-0 bg-surface opacity-70" aria-hidden />
            <span className="relative flex items-center gap-2.5 rounded-lg bg-surface px-4 py-2.5 shadow-card">
              <Giro grande />
              <span className="text-[12.5px] font-semibold">Salvando alterações...</span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

/* Anel girando.

   `animate-spin` e do proprio Tailwind, entao nao ha keyframe a manter aqui - e
   ele respeita o `prefers-reduced-motion` global do theme/global.css, que zera a
   duracao de toda animacao. Nesse caso o anel fica parado, e o texto ao lado
   continua dizendo o que esta acontecendo: a informacao nunca depende so do
   movimento. */
function Giro({ grande = false }: { grande?: boolean }) {
  const d = grande ? 18 : 13;
  return (
    <svg width={d} height={d} viewBox="0 0 24 24" fill="none" className="animate-spin" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.6" opacity="0.25" />
      <path d="M21 12a9 9 0 00-9-9" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}
